import React, { useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, Archive, Pencil, CheckCircle2, Check, Users } from "lucide-react";
import { api } from "@/lib/api";
import LoadError from "@/components/LoadError";
import { useAuth } from "@/lib/auth";
import dayjs from "dayjs";
import { toast } from "sonner";

const CATEGORY_LABEL = { general: "General", safety: "Safety", hr: "HR", ops: "Ops" };
const CATEGORY_COLOR = { safety: "bg-brand-danger/10 text-brand-danger", hr: "bg-blue-100 text-blue-800", ops: "bg-brand-warn/15 text-brand-charcoal", general: "bg-brand-stone text-brand-charcoal" };

export default function AnnouncementsPage() {
    const { employee } = useAuth();
    const canManage = employee?.role === "manager" || employee?.role === "admin";

    const [items, setItems] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ title: "", body: "", category: "general", requires_ack: false });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({ title: "", body: "", category: "general", requires_ack: false });
    const [ackingId, setAckingId] = useState(null);
    const [rosterId, setRosterId] = useState(null);
    const [roster, setRoster] = useState(null);

    const [err, setErr] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const load = async () => {
        setErr(null);
        try {
            const r = await api.get(`/announcements${showArchived ? "?include_archived=true" : ""}`);
            setItems(r.data);
            setLoaded(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load the news feed.");
        }
    };

    useEffect(() => { load(); }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!draft.title.trim() || !draft.body.trim()) { toast.error("Title and body are required"); return; }
        try {
            await api.post("/announcements", draft);
            setDraft({ title: "", body: "", category: "general", requires_ack: false });
            setAdding(false);
            await load();
            toast.success("Announcement posted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post"); }
    };

    const ackAnn = async (a) => {
        setAckingId(a.announcement_id);
        try {
            await api.post(`/announcements/${a.announcement_id}/ack`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
        finally { setAckingId(null); }
    };

    const toggleRoster = async (a) => {
        if (rosterId === a.announcement_id) { setRosterId(null); setRoster(null); return; }
        setRosterId(a.announcement_id);
        setRoster(null);
        try {
            const r = await api.get(`/announcements/${a.announcement_id}/acks`);
            setRoster(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); setRosterId(null); }
    };

    const deleteAnn = async (a) => {
        if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/announcements/${a.announcement_id}`);
            await load();
            toast.success("Deleted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const archiveAnn = async (a) => {
        try {
            await api.patch(`/announcements/${a.announcement_id}/archive`);
            await load();
            toast.success("Retired — off the crew's News list");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not retire this post"); }
    };

    const startEdit = (a) => {
        setEditingId(a.announcement_id);
        setEditDraft({ title: a.title, body: a.body, category: a.category, requires_ack: !!a.requires_ack });
    };

    const saveEdit = async () => {
        if (!editDraft.title.trim() || !editDraft.body.trim()) { toast.error("Title and body are required"); return; }
        try {
            await api.patch(`/announcements/${editingId}`, editDraft);
            setEditingId(null);
            await load();
            toast.success("Announcement updated");
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };

    if (err && !loaded) return <LoadError message={err} onRetry={load} testId="announcements-error" />;

    return (
        <div className="paper-bg pb-28" data-testid="announcements-page">
            <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-bold text-2xl flex items-center gap-2">
                        <Megaphone className="h-5 w-5 text-brand-navy" /> Announcements
                    </h2>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setShowArchived(!showArchived)} className={`text-xs px-3 py-1.5 rounded-full border font-bold transition ${showArchived ? "bg-brand-charcoal text-brand-bone border-brand-charcoal" : "bg-white text-brand-charcoal border-brand-charcoal/20"}`} data-testid="toggle-archived">
                            {showArchived ? "Hide retired posts" : "Show retired posts"}
                        </button>
                        {canManage && (
                            <button onClick={() => setAdding(!adding)} className="h-9 px-3 rounded-xl bg-brand-navy text-brand-bone font-bold text-xs flex items-center gap-1.5" data-testid="add-announcement-button">
                                <Plus className="h-3.5 w-3.5" /> Post
                            </button>
                        )}
                    </div>
                </div>

                {/* Compose form */}
                {adding && canManage && (
                    <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 space-y-3" data-testid="add-announcement-form">
                        <input
                            placeholder="Title"
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            className="h-12 w-full border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal focus:ring-2 focus:ring-brand-navy outline-none"
                            data-testid="ann-title-input"
                        />
                        <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="h-11 w-full border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="ann-category-input">
                            <option value="general">General</option>
                            <option value="safety">Safety</option>
                            <option value="hr">HR</option>
                            <option value="ops">Ops</option>
                        </select>
                        <textarea
                            placeholder="Message…"
                            value={draft.body}
                            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                            rows={4}
                            className="w-full border border-brand-charcoal/20 rounded-xl px-3 py-3 text-brand-charcoal focus:ring-2 focus:ring-brand-navy outline-none resize-none"
                            data-testid="ann-body-input"
                        />
                        <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl bg-brand-stone/60 p-3" data-testid="ann-requires-ack-label">
                            <input
                                type="checkbox"
                                checked={draft.requires_ack}
                                onChange={(e) => setDraft({ ...draft, requires_ack: e.target.checked })}
                                className="mt-0.5 h-5 w-5 accent-brand-navy shrink-0"
                                data-testid="ann-requires-ack-input"
                            />
                            <span className="text-sm text-brand-charcoal">
                                <span className="font-bold">Require the team to confirm they read this</span>
                                <span className="block text-brand-charcoal/60 text-xs mt-0.5">Everyone gets a "I read this" button. You'll see who has and hasn't.</span>
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <button onClick={submit} className="h-11 px-5 rounded-xl bg-brand-navy text-brand-bone font-bold" data-testid="post-announcement-button">Post</button>
                            <button onClick={() => setAdding(false)} className="h-11 px-5 rounded-xl bg-brand-stone">Cancel</button>
                        </div>
                    </div>
                )}

                {/* List */}
                {items.length === 0 && (
                    <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-10 text-center text-brand-charcoal/50" data-testid="no-announcements">
                        No announcements yet.
                    </div>
                )}
                <ul className="space-y-4">
                    {items.map((a) => (
                        <li key={a.announcement_id} className={`bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 ${a.archived ? "opacity-60" : ""}`} data-testid={`ann-${a.announcement_id}`}>
                            {editingId === a.announcement_id ? (
                                <div className="space-y-3" data-testid={`edit-announcement-form-${a.announcement_id}`}>
                                    <input
                                        value={editDraft.title}
                                        onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                                        className="h-12 w-full border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal focus:ring-2 focus:ring-brand-navy outline-none"
                                        data-testid="edit-ann-title-input"
                                    />
                                    <select value={editDraft.category} onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })} className="h-11 w-full border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="edit-ann-category-input">
                                        <option value="general">General</option>
                                        <option value="safety">Safety</option>
                                        <option value="hr">HR</option>
                                        <option value="ops">Ops</option>
                                    </select>
                                    <textarea
                                        value={editDraft.body}
                                        onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })}
                                        rows={4}
                                        className="w-full border border-brand-charcoal/20 rounded-xl px-3 py-3 text-brand-charcoal focus:ring-2 focus:ring-brand-navy outline-none resize-none"
                                        data-testid="edit-ann-body-input"
                                    />
                                    <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl bg-brand-stone/60 p-3">
                                        <input
                                            type="checkbox"
                                            checked={editDraft.requires_ack}
                                            onChange={(e) => setEditDraft({ ...editDraft, requires_ack: e.target.checked })}
                                            className="mt-0.5 h-5 w-5 accent-brand-navy shrink-0"
                                            data-testid="edit-ann-requires-ack-input"
                                        />
                                        <span className="text-sm text-brand-charcoal">
                                            <span className="font-bold">Require the team to confirm they read this</span>
                                        </span>
                                    </label>
                                    <div className="flex gap-2">
                                        <button onClick={saveEdit} className="h-11 px-5 rounded-xl bg-brand-navy text-brand-bone font-bold" data-testid="save-edit-announcement-button">Save</button>
                                        <button onClick={() => setEditingId(null)} className="h-11 px-5 rounded-xl bg-brand-stone">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${CATEGORY_COLOR[a.category] || CATEGORY_COLOR.general}`}>
                                            {CATEGORY_LABEL[a.category] || a.category}
                                        </span>
                                        {a.archived && <span className="text-[10px] uppercase tracking-widest bg-brand-stone/50 text-brand-steel px-2 py-0.5 rounded-full font-bold">Retired</span>}
                                        {a.edited && (
                                            <span
                                                className="text-[10px] uppercase tracking-widest bg-brand-gold/30 text-brand-charcoal px-2 py-0.5 rounded-full font-bold"
                                                data-testid={`edited-badge-${a.announcement_id}`}
                                                title={a.edited_at ? `Edited ${dayjs(a.edited_at).format("MMM D, YYYY h:mm A")}` : "Edited"}
                                            >
                                                Edited
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-bold text-xl mt-2">{a.title}</h3>
                                    <p className="mt-2 text-brand-charcoal/80 whitespace-pre-wrap text-sm">{a.body}</p>
                                    <div className="text-[11px] text-brand-charcoal/40 mt-3">
                                        {a.author_name && <span>{a.author_name} · </span>}
                                        {dayjs(a.created_at).format("MMM D, YYYY")}
                                    </div>

                                    {a.requires_ack && (
                                        <div className="mt-4 pt-3 border-t border-brand-charcoal/10 space-y-3" data-testid={`ack-section-${a.announcement_id}`}>
                                            {a.acked_by_me ? (
                                                <div className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-success" data-testid={`acked-badge-${a.announcement_id}`}>
                                                    <CheckCircle2 className="h-4 w-4" /> You confirmed you read this
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => ackAnn(a)}
                                                    disabled={ackingId === a.announcement_id}
                                                    className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-brand-navy text-brand-bone font-bold disabled:opacity-60"
                                                    data-testid={`ack-button-${a.announcement_id}`}
                                                >
                                                    <Check className="h-4 w-4" /> {ackingId === a.announcement_id ? "Saving…" : "I read this"}
                                                </button>
                                            )}

                                            {canManage && typeof a.ack_count === "number" && (
                                                <div>
                                                    <button
                                                        onClick={() => toggleRoster(a)}
                                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-charcoal/70 hover:text-brand-charcoal hover:underline"
                                                        data-testid={`roster-toggle-${a.announcement_id}`}
                                                    >
                                                        <Users className="h-3.5 w-3.5" />
                                                        {a.ack_count} of {a.total_audience} read
                                                    </button>
                                                    {rosterId === a.announcement_id && (
                                                        <div className="mt-2 rounded-xl bg-brand-stone/60 p-3 text-xs" data-testid={`roster-${a.announcement_id}`}>
                                                            {!roster ? (
                                                                <span className="text-brand-charcoal/50">Loading…</span>
                                                            ) : (
                                                                <div className="space-y-2">
                                                                    <div>
                                                                        <div className="uppercase tracking-widest text-brand-success font-bold mb-1">Read ({roster.read.length})</div>
                                                                        {roster.read.length === 0
                                                                            ? <div className="text-brand-charcoal/50">No one yet.</div>
                                                                            : roster.read.map((p) => (
                                                                                <div key={p.employee_id} className="flex justify-between">
                                                                                    <span className="text-brand-charcoal">{p.name}</span>
                                                                                    <span className="text-brand-charcoal/50">{dayjs(p.acked_at).format("MMM D, h:mm A")}</span>
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                    <div>
                                                                        <div className="uppercase tracking-widest text-brand-danger font-bold mb-1">Not yet ({roster.unread.length})</div>
                                                                        {roster.unread.length === 0
                                                                            ? <div className="text-brand-success font-semibold">Everyone's covered. 🦅</div>
                                                                            : roster.unread.map((p) => (
                                                                                <div key={p.employee_id} className="text-brand-charcoal">{p.name}</div>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {canManage && (
                                    <div className="flex flex-col gap-1.5 shrink-0">
                                        <button onClick={() => startEdit(a)} className="h-8 px-2.5 rounded-lg bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-xs flex items-center gap-1" data-testid={`edit-ann-${a.announcement_id}`} title="Edit this announcement">
                                            <Pencil className="h-3.5 w-3.5" /> Edit
                                        </button>
                                        {!a.archived && (
                                            <button onClick={() => archiveAnn(a)} className="h-8 px-2.5 rounded-lg bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-xs flex items-center gap-1" data-testid={`archive-ann-${a.announcement_id}`} title="Retire this post — it comes off the crew's News list for everyone, but is not deleted">
                                                <Archive className="h-3.5 w-3.5" /> Retire
                                            </button>
                                        )}
                                        <button onClick={() => deleteAnn(a)} className="h-8 px-2.5 rounded-lg bg-brand-danger/10 hover:bg-brand-danger/10 text-brand-danger font-bold text-xs flex items-center gap-1" data-testid={`delete-ann-${a.announcement_id}`} title="Delete permanently">
                                            <Trash2 className="h-3.5 w-3.5" /> Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
